import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Auth } from '@aws-amplify/auth';
import { TranslocoService } from '@ngneat/transloco';
import { from, Observable } from 'rxjs';
import { mergeMap, tap } from 'rxjs/operators';
import { map } from 'rxjs/operators/map';
import { isNullOrUndefined } from 'util';
import { Patient } from './../../../core/api/patient.service';
import { AppSyncService } from './../../../core/appsync.service';
import { DryEyeFormGroup } from './dry-eye-form/dry-eye-form.model';
import { DryEyeMedicalHistoryFormGroup } from './dry-eye-medical-history-form/dry-eye-medical-history-form.model';

@Injectable({
  providedIn: 'root'
})
export class DryEyePredictionService {
  medicalHistoryFormGroup: DryEyeMedicalHistoryFormGroup;
  symptomsAndSignsFormGroup: DryEyeFormGroup;
  patient: Patient;

  constructor(
    private appSyncService: AppSyncService,
    private http: HttpClient,
    private translocoService: TranslocoService
  ) {}

  public predictDryEyeCategoryAndSeverity(): Observable<any> {
    const errors = this.getInputErrors();
    if (errors && errors.length > 0) {
      return new Observable(observer => {
        observer.error(errors);
        observer.complete();
      });
    }

    const dryEyeFormCombined = {
      ...this.medicalHistoryFormGroup.value,
      ...this.symptomsAndSignsFormGroup.value
    };

    const dryEyePredictionInput: any = {
      ...dryEyeFormCombined,
      age: Math.floor((Date.now() - new Date(this.patient.dateOfBirth).getTime()) / 3.15576e10),
      gender: this.patient.gender
    };

    return from(Auth.currentSession()).pipe(
      mergeMap(res => {
        const headers = new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${res.getIdToken().getJwtToken()}`
        });
        return this.http.post(
          'https://lnh8cl4q2k.execute-api.us-east-1.amazonaws.com/default/DryEyeCategoryAndSeverityPrediction',
          JSON.stringify(dryEyePredictionInput),
          { headers }
        );
      })
    );
  }

  getInputErrors(): string[] {
    const errors = [];

    if (!this.symptomsAndSignsFormGroup.controls.speed) {
      errors.push(this.translocoService.translate('PREDICT_ERRORS.SPEED_HIDDEN'));
    }
    if (
      !this.symptomsAndSignsFormGroup.controls.osdi &&
      !this.symptomsAndSignsFormGroup.controls.osdi6
    ) {
      errors.push(this.translocoService.translate('PREDICT_ERRORS.OSDI_HIDDEN'));
    }

    if (errors.length > 0) {
      return errors;
    }

    if (
      isNullOrUndefined(this.symptomsAndSignsFormGroup.controls.speed.value) ||
      this.symptomsAndSignsFormGroup.controls.speed.value > 28
    ) {
      errors.push(this.translocoService.translate('PREDICT_ERRORS.SPEED_REQUIRED'));
    }
    if (
      (isNullOrUndefined((this.symptomsAndSignsFormGroup.controls.osdi || {})['value']) &&
        isNullOrUndefined((this.symptomsAndSignsFormGroup.controls.osdi6 || {})['value'])) ||
      (this.symptomsAndSignsFormGroup.controls.osdi || {})['value'] > 100 ||
      (this.symptomsAndSignsFormGroup.controls.osdi6 || {})['value'] > 24
    ) {
      errors.push(this.translocoService.translate('PREDICT_ERRORS.OSDI_REQUIRED'));
    }

    const MIN_SIGNS = 8;
    const validSignsCount = Object.entries(this.symptomsAndSignsFormGroup.controls).filter(
      ([key, ctrl]) =>
        (key.startsWith('LEFT_') || key.startsWith('RIGHT_')) &&
        !key.includes('_METHOD') &&
        ctrl.value != null &&
        ctrl.valid
    ).length;

    if (errors.length === 0 && validSignsCount < MIN_SIGNS) {
      const missing = MIN_SIGNS - validSignsCount;
      errors.push(
        this.translocoService.translate('PREDICT_ERRORS.MORE_SIGNS_REQUIRED', {
          missing: missing,
          minSigns: MIN_SIGNS
        })
      );
    }

    return errors.length ? errors : null;
  }

  predictDryEyeSeverity(): Observable<string> {
    const bodyKeys = [
      'RIGHT_NITBUT',
      'LEFT_NITBUT',
      'RIGHT_ITBUT',
      'LEFT_ITBUT',
      'RIGHT_OSMOLARITY',
      'LEFT_OSMOLARITY',
      'LEFT_MEIBOGRAPHY',
      'RIGHT_MEIBOGRAPHY',
      'speed'
    ];

    const body = bodyKeys.reduce((acc, key) => {
      acc[key] = this.symptomsAndSignsFormGroup.controls[key].value || null;
      return acc;
    }, {});

    return from(Auth.currentSession()).pipe(
      mergeMap(res => {
        const headers = new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${res.getIdToken().getJwtToken()}`
        });
        return this.http
          .post(
            'https://ziicgfn8u9.execute-api.us-east-1.amazonaws.com/default/DryEyePreOpScreeningPrediction',
            JSON.stringify(body),
            { headers }
          )
          .pipe(tap(console.log));
      }),
      map(res => res['body'])
    );
  }
}
